import { useEffect, useRef } from 'react'
import {
  calculateMaterialsFromWriteup,
  getFormulaKeysForSection,
} from '@/lib/material-formulas'
import { lchService, type LchRow } from '@/services/lch-service'
import type { LineItem, PricingItem } from '@/types'

/**
 * Parent-level cleanup hook that removes orphaned formula-driven line items
 * and H&D rows when writeup rows are unchecked.
 *
 * Problem: The per-tab `useMaterialCalculations` hook only runs when a tab is
 * mounted. If a user unchecks a writeup row while on the Writeup tab, the
 * corresponding Concrete/Asphalt/Grading tab is unmounted and its cleanup
 * never fires — leaving orphaned items in the database.
 *
 * Solution: This hook runs at the estimate-builder-page level (always mounted)
 * and watches scopeData. When it changes, it checks ALL sections for orphaned
 * formula-driven materials and H&D rows, then deletes them.
 */

const SECTIONS_WITH_FORMULAS = ['asphalt', 'concrete', 'grading', 'seal-coat']

interface WriteupField { value: string; size: string; placeholder?: string }
interface WriteupRow { id: string; enabled: boolean; content: (string | WriteupField)[] }
interface WriteupSection { id: string; rows: WriteupRow[] }
interface WriteupGroup { id: string; rows: WriteupRow[]; sections: WriteupSection[] }

/** Collect all enabled writeup row IDs from scopeData */
function getEnabledRowIds(scopeData: string | undefined): Set<string> {
  if (!scopeData) return new Set()
  try {
    const groups: WriteupGroup[] = JSON.parse(scopeData)
    const ids = new Set<string>()
    for (const group of groups) {
      for (const row of group.rows || []) {
        if (row.enabled) ids.add(row.id)
      }
      for (const sec of group.sections || []) {
        for (const row of sec.rows || []) {
          if (row.enabled) ids.add(row.id)
        }
      }
    }
    return ids
  } catch {
    return new Set()
  }
}

/**
 * Given a section, its writeup-derived expected materials, and the tenant's pricing
 * catalog, returns the ids of material line items that are safe to auto-delete:
 * formula-driven items (linked to a pricing item whose formulaKey is produced by
 * this section) whose writeup row is no longer enabled/filled.
 *
 * Linkage is via `pricingItemId` → `PricingItem.formulaKey`, NOT the item's
 * description text — a manually-added item whose description happens to match a
 * formula's display name (e.g. "Class 2 Base") has no pricingItemId and is never
 * touched. See root CLAUDE.md "Saved User Data Is Sacred".
 */
export function findOrphanedMaterialItemIds(
  section: string,
  scopeData: string | undefined,
  lineItems: LineItem[],
  pricingItems: PricingItem[],
): string[] {
  const calculations = calculateMaterialsFromWriteup(scopeData, section)
  const expectedFormulaKeys = new Set(calculations.map((c) => c.formulaKey))
  const sectionFormulaKeys = getFormulaKeysForSection(section)

  const pricingById = new Map<string, PricingItem>()
  for (const p of pricingItems) pricingById.set(p.id, p)

  const sectionMaterials = lineItems.filter(
    (li) => li.category === 'materials' && li.section === (section || null)
  )

  const orphanedIds: string[] = []
  for (const li of sectionMaterials) {
    if (!li.pricingItemId) continue // Manually added — no linkage, never touch

    const formulaKey = pricingById.get(li.pricingItemId)?.formulaKey
    if (!formulaKey || !sectionFormulaKeys.has(formulaKey)) continue // Not formula-driven for this section

    if (!expectedFormulaKeys.has(formulaKey)) {
      orphanedIds.push(li.id)
    }
  }
  return orphanedIds
}

interface UseOrphanedItemCleanupProps {
  estimateId: string | undefined
  scopeData: string | undefined
  lineItems: LineItem[]
  pricingItems: PricingItem[]
  onDeleteItem: (itemId: string) => Promise<void>
}

export function useOrphanedItemCleanup({
  estimateId,
  scopeData,
  lineItems,
  pricingItems,
  onDeleteItem,
}: UseOrphanedItemCleanupProps) {

  const lineItemsRef = useRef(lineItems)
  lineItemsRef.current = lineItems

  const pricingItemsRef = useRef(pricingItems)
  pricingItemsRef.current = pricingItems

  const processingRef = useRef(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!estimateId || !scopeData) return
    // Don't run if lineItems haven't loaded yet
    if (lineItems.length === 0) return

    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(async () => {
      if (processingRef.current) return
      processingRef.current = true

      try {
        const currentLineItems = lineItemsRef.current
        const currentPricingItems = pricingItemsRef.current

        // ── Phase 1: Clean orphaned formula-driven materials ──
        for (const section of SECTIONS_WITH_FORMULAS) {
          const orphanedIds = findOrphanedMaterialItemIds(section, scopeData, currentLineItems, currentPricingItems)
          for (const id of orphanedIds) {
            await onDeleteItem(id)
          }
        }

        // ── Phase 2: Clean orphaned H&D rows ──
        const enabledRowIds = getEnabledRowIds(scopeData)
        try {
          const savedRows = await lchService.getEstimateLchData(estimateId)
          if (savedRows && Array.isArray(savedRows)) {
            const lchRows = savedRows as LchRow[]
            const cleaned = lchRows.filter(r => {
              // Keep rows without writeupRowId (manually added)
              if (!r.writeupRowId) return true
              // Keep rows whose writeup row is still enabled
              return enabledRowIds.has(r.writeupRowId)
            })

            if (cleaned.length < lchRows.length) {
              await lchService.updateEstimateLchData(estimateId, cleaned)
            }
          }
        } catch {
          // H&D cleanup failed — non-fatal
        }
      } finally {
        processingRef.current = false
      }
    }, 2000) // Slightly longer debounce than per-tab hook to avoid conflicts

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [scopeData, estimateId, lineItems.length])
}
